import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import { AllocationGuideAmountsTable } from '@/features/allocation-guides/AllocationGuideAmountsTable'
import { UserBreadcrumbs } from './user-record/UserBreadcrumbs'
import {
  formatCurrency,
  centsToDollars,
  addCurrencyArray,
} from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { fetchAdminUserAllocationGuides } from '@/actions/allocationGuideActions'
import { getAllocationGuideById } from '@/selectors/allocationGuideSelectors'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'
import {
  Text,
  Breadcrumb,
  Label,
  Separator,
  Card,
  CardColors,
  Icon,
} from '@/ui'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'

export const AdminAllocationGuidePanel = () => {
  const dispatch = useAppDispatch()
  const { userId, allocationGuideId } = useParams()
  const allocationGuide = useReselector(
    getAllocationGuideById,
    allocationGuideId
  )

  useEffect(() => {
    if (userId) {
      dispatch(fetchAdminUserAllocationGuides(userId))
    }
  }, [dispatch, userId])

  const notification = useMemo(() => {
    if (allocationGuide?.userNotifiedAt) {
      return (
        <Label color="green">
          Notified:{' '}
          {DateTime.fromISO(
            String(allocationGuide?.userNotifiedAt ?? '')
          ).toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}
        </Label>
      )
    } else {
      return <Label color="orange">Not Notified</Label>
    }
  }, [allocationGuide?.userNotifiedAt])

  const profits = addCurrencyArray([
    centsToDollars(allocationGuide?.incomeAtCompilationInCents),
    centsToDollars(allocationGuide?.expensesAtCompilationInCents),
  ])

  return (
    <div id="adminPanel">
      <UserBreadcrumbs userId={userId} pageName={'Allocation Guides'}>
        <Breadcrumb.Item>
          <Breadcrumb.Link>
            <Icon icon={faChevronRight} />
          </Breadcrumb.Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          {DateTime.fromISO(allocationGuide?.date ?? '').toFormat(
            DATE_FORMATS_LUXON.MONTH_YEAR
          )}
        </Breadcrumb.Item>
      </UserBreadcrumbs>
      <Separator />

      <Card color={CardColors.LEVEL_1}>
        <div className="flexbox">
          <Text variant={TypographyStyles.H3}>
            {DateTime.fromISO(allocationGuide?.date ?? '').toFormat(
              DATE_FORMATS_LUXON.MONTH_YEAR
            )}{' '}
            Allocation Guide
          </Text>
          <div>
            <p>
              <b>Compiled On:</b>{' '}
              {DateTime.fromISO(allocationGuide?.compiledOn ?? '').toFormat(
                DATE_FORMATS_LUXON.DISPLAY_LONG
              )}
            </p>
            <p>
              <b>Notified On:</b> {notification}
            </p>
            <p>
              <b>User ID:</b> {allocationGuide?.userId}
            </p>
          </div>
        </div>
        <Separator />
        <div className="flex flex-row gap-4">
          <div className="w-full md:w-1/2">
            {allocationGuide && (
              <Card color={CardColors.LEVEL_0}>
                <AllocationGuideAmountsTable
                  allocationGuide={allocationGuide}
                  profits={profits}
                />
              </Card>
            )}
          </div>

          <div className="w-full md:w-1/2">
            <Card color={CardColors.LEVEL_0}>
              <Text variant={TypographyStyles.H3}>Profit and Loss*</Text>
              <Text variant={TypographyStyles.BODY_SM}>
                *at time of allocation guide creation
              </Text>

              <div className="flex flex-row justify-between my-2">
                <Text variant={TypographyStyles.BODY_MD}>Income</Text>
                <Text variant={TypographyStyles.BODY_MD}>
                  <CurrencyFormatLabel
                    value={formatCurrency(
                      centsToDollars(
                        allocationGuide?.incomeAtCompilationInCents
                      )
                    )}
                  />
                </Text>
              </div>

              <div className="flex justify-between mb-2">
                <Text variant={TypographyStyles.BODY_MD}>Expenses</Text>
                <Text variant={TypographyStyles.BODY_MD}>
                  <CurrencyFormatLabel
                    value={formatCurrency(
                      centsToDollars(
                        allocationGuide?.expensesAtCompilationInCents
                      )
                    )}
                  />
                </Text>
              </div>

              <div className="flex justify-between">
                <Text variant={TypographyStyles.BODY_MD}>Profit</Text>
                <Text variant={TypographyStyles.BODY_MD}>
                  <CurrencyFormatLabel value={formatCurrency(profits)} />
                </Text>
              </div>
            </Card>
          </div>
        </div>
      </Card>
    </div>
  )
}
